/* ============================================================================
   tpw-rollin.js — one-time digit roll-IN on scroll-into-view (F-24)
   ----------------------------------------------------------------------------
   The Genius Bar / cart / PDP numerals roll on CHANGE. Bottom-of-page figures
   (footer stat row, store-hours counts, ratings) are STATIC — they never change,
   so the on-brand analogue is a single reveal-roll the first time the element
   scrolls into view: the digits settle from a zeroed baseline into their true
   value, once, then never again.

   Wiring:
     · tag a numeral element with  data-tpw-rollin  (its textContent = target)
     · this observer fires ONE roll per element on first viewport entry, then
       unobserves it (no loop, no re-trigger on scroll away + back)

   Reuses the shared window.TPWRoll odometer (assets/tpw-roll.js). The roll only
   decorates the reveal — the element's real text is the source of truth and is
   committed up front by TPWRoll (last reel cell = target), so a starved frame can
   never leave a stale/blank figure. prefers-reduced-motion -> we leave the literal
   text untouched (already correct on screen) = zero motion, zero risk.

   Accessibility: the reels are presentational (aria-hidden-equivalent spans); we
   only reshape the visible numerals. Any .visually-hidden sr mirror is untouched.
   Zero CLS: the box never changes size; only inner cells translate.
   ============================================================================ */
(function () {
  'use strict';

  var SEL = '[data-tpw-rollin]';

  function reduced() {
    return window.TPWRoll ? window.TPWRoll.reduced()
      : (window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches);
  }

  // Build the zeroed baseline: same string, every DIGIT -> 0 (so "$250+" -> "$000+",
  // "5.0" -> "0.0"). Non-digit glyphs stay put so the shape/width never shifts.
  function zeroed(text) {
    return String(text).replace(/\d/g, '0');
  }

  function rollIn(el) {
    if (!el || el.getAttribute('data-tpw-rollin-done') === '1') return;
    el.setAttribute('data-tpw-rollin-done', '1');
    var target = (el.textContent || '').trim();
    if (!target) return;
    // Nothing to roll if there are no digits.
    if (!/\d/.test(target)) return;
    if (!window.TPWRoll || reduced()) {
      // Leave the literal text as-is (already the correct value on screen).
      return;
    }
    // Shape to the zeroed baseline instantly, then roll to the true value next frame
    // so the reels exist in the DOM before they animate (no first-frame jump).
    window.TPWRoll.set(el, zeroed(target));
    requestAnimationFrame(function () {
      window.TPWRoll.roll(el, target, { from: zeroed(target) });
    });
  }

  function init() {
    var els = document.querySelectorAll(SEL);
    if (!els.length) return;

    // Reduced motion or no observer support: leave literal text (correct value shown).
    if (reduced() || !('IntersectionObserver' in window)) return;

    // threshold kept low (0.25): the stat figures are short ~50px cells, so a high
    // threshold can fail to trip when the row sits near the viewport edge on landscape
    // / short viewports. 25% visible is enough to read the reveal as deliberate, and
    // we unobserve on first fire so it still only ever happens once.
    var io = new IntersectionObserver(function (entries) {
      entries.forEach(function (entry) {
        if (entry.isIntersecting) {
          rollIn(entry.target);
          io.unobserve(entry.target); // once only
        }
      });
    }, { threshold: 0.25 });

    [].forEach.call(els, function (el) { io.observe(el); });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
